import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, NavLink as NavLinkType, hasUpcomingEvent } from "@/data/site";

function DropdownMenu({ item }: { item: NavLinkType }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.to) && item.to !== "/";

  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(true);
  };
  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NavLink
        to={item.to}
        className={`flex items-center gap-1 text-sm font-medium tracking-wide link-underline ${
          isActive ? "text-[#FFC93A]" : "text-white/80 hover:text-white"
        }`}
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </NavLink>

      {open && item.children && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50 w-[640px]">
          <div className="bg-[#0d1f3a] border border-white/10 shadow-2xl shadow-black/40">
            {/* Top accent line */}
            <div className="h-0.5 bg-gradient-to-r from-[#FFC93A]/0 via-[#FFC93A] to-[#FFC93A]/0" />
            <div className="p-3">
              {/* 2-column grid — scales to more items without growing vertically */}
              <div className="grid grid-cols-2 gap-0.5">
                {item.children.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-white/05 transition-colors group rounded-sm"
                    onClick={() => setOpen(false)}
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FFC93A]/40 group-hover:bg-[#FFC93A] mt-2 flex-shrink-0 transition-colors" />
                    <div>
                      <div className="text-sm font-semibold text-white/90 group-hover:text-[#FFC93A] transition-colors leading-snug">
                        {child.label}
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5 leading-snug">{child.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
              {/* Footer link */}
              <div className="border-t border-white/08 mt-2 pt-2 px-4 pb-1">
                <Link
                  to={item.to}
                  className="text-[11px] text-white/40 hover:text-[#FFC93A] transition-colors flex items-center gap-1.5 font-medium uppercase tracking-wider"
                  onClick={() => setOpen(false)}
                >
                  View all {item.label.toLowerCase()} <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const eventLive = hasUpcomingEvent();
  const centerLinks = NAV_LINKS.filter((l) => l.to !== "/" && l.to !== "/contact");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A1628]/95 backdrop-blur-md border-b border-white/10" : "bg-[#0A1628]/80 backdrop-blur-sm"
      }`}
      data-testid="site-nav"
    >
      <div className="px-6 sm:px-10 lg:px-16 xl:px-20 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link">
          <Logo className="h-10" onDark />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {centerLinks.map((l) =>
            l.children ? (
              <DropdownMenu key={l.to} item={l} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide link-underline ${isActive ? "text-[#FFC93A]" : "text-white/80 hover:text-white"}`
                }
              >
                {l.label}
                {l.to === "/events" && eventLive && <span className="relative inline-flex w-2 h-2 ml-2 align-middle" aria-label="An event is coming up"><span className="absolute inset-0 rounded-full bg-[#FFC93A] animate-ping opacity-80" /><span className="relative w-2 h-2 rounded-full bg-[#FFC93A]" /></span>}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <NavLink
            to="/contact"
            data-testid="nav-cta-contact"
            className="group inline-flex items-center gap-2 bg-[#FFC93A] text-[#0A1628] px-5 py-3 text-sm font-bold tracking-tight rounded-none btn-sharp hover:bg-white transition-colors"
          >
            Talk to Us
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </NavLink>
        </div>

        <button
          className="lg:hidden text-white p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0A1628] border-t border-white/10 px-5 py-6 space-y-1" data-testid="nav-mobile-panel">
          {NAV_LINKS.map((l) =>
            l.children ? (
              <div key={l.to}>
                <button
                  className="w-full flex items-center justify-between text-base py-3 border-b border-white/5 text-white/85 font-medium"
                  onClick={() => setMobileServicesOpen((v) => !v)}
                >
                  {l.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="pl-4 py-2 space-y-1 border-b border-white/5">
                    {l.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={({ isActive }) =>
                          `block text-sm py-2 ${isActive ? "text-[#FFC93A] font-semibold" : "text-white/70"}`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                    <NavLink
                      to={l.to}
                      className="block text-xs py-2 text-[#FFC93A] font-medium uppercase tracking-wider"
                    >
                      View all {l.label.toLowerCase()} →
                    </NavLink>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `block text-base py-3 border-b border-white/5 last:border-b-0 ${isActive ? "text-[#FFC93A] font-semibold" : "text-white/85"}`
                }
                data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
                {l.to === "/events" && eventLive && <span className="relative inline-flex w-2 h-2 ml-2 align-middle" aria-label="An event is coming up"><span className="absolute inset-0 rounded-full bg-[#FFC93A] animate-ping opacity-80" /><span className="relative w-2 h-2 rounded-full bg-[#FFC93A]" /></span>}
              </NavLink>
            )
          )}
          <NavLink
            to="/contact"
            className="mt-5 inline-flex items-center gap-2 bg-[#FFC93A] text-[#0A1628] px-5 py-3 text-sm font-bold rounded-none"
          >
            Talk to Us <ArrowUpRight className="w-4 h-4" />
          </NavLink>
        </div>
      )}
    </header>
  );
}
